import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../contexts/authContext';
import { Box, Container, Typography } from '@material-ui/core';
import { getAllSteerages } from '../../services/steerageService';
import { Severity } from '../../types';
import useSnackbar from '../../hooks/useSnackbar';
import TenantSelector from '../../components/TenantSelector';
import { FFTable, FFTableColumn } from '../../components/FFTable';
import {
  ListFilterBar,
  ListFilters,
  PAST_DATE_OPTIONS,
  SelectFilter,
} from '../../components/ListFilterBar/ListFilterBar';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { MDButton } from 'components/MDButton';

// Extended SteerageData for display purposes
interface DisplaySteerageData {
  id: number;
  createdAt: Date;
  caseId: number | null;
  providerType: string;
  policyNumber: string | null;
  providerName: string;
  providerAddress: string;
  steeredBy: string;
  admissionStatus: string;
  networkName?: string | null;
  networkLogoUrl?: string | null;
}

type SteeragePageData = {
  entities: DisplaySteerageData[];
  isLoading: boolean;
  error: null | Error;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

type SteerageFilters = ListFilters & { providerType: string; admissionStatus: string };

const DEFAULT_STEERAGE_FILTERS: SteerageFilters = {
  search: '',
  providerType: '',
  admissionStatus: '',
  datePreset: 'all',
};

// Values are the labels the list shows; the API maps them to what is stored.
const STEERAGE_SELECTS: SelectFilter[] = [
  {
    key: 'providerType',
    id: 'steerage-provider-type',
    label: 'Provider Type',
    options: [
      { value: '', label: 'All provider types' },
      { value: 'ER', label: 'ER' },
      { value: 'Urgent Care', label: 'Urgent Care' },
      { value: 'Hospital', label: 'Hospital' },
      { value: 'Dental', label: 'Dental' },
    ],
  },
  {
    key: 'admissionStatus',
    id: 'steerage-admission-status',
    label: 'Admission Status',
    options: [
      { value: '', label: 'All admission statuses' },
      { value: 'Member confirmed', label: 'Member confirmed' },
      { value: 'Member rejected', label: 'Member rejected' },
      { value: 'Pending', label: 'Pending' },
    ],
  },
];

/** The query a set of filters stands for, shared by the list and the export. */
const listQuery = (f: SteerageFilters) => ({
  fromDate: f.fromDate,
  untilDate: f.untilDate,
  filters: {
    search: f.search,
    providerType: f.providerType,
    admissionStatus: f.admissionStatus,
  },
});

type QueryState = {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

const providerTypeColors: Record<string, string> = {
  ER: '#e53935', // Red
  'Urgent Care': '#43a047', // Orange
  Hospital: '#fb8c00', // Blue
  Dental: '#1e88e5', // Green
};

const admissionStatusColors: Record<string, string> = {
  // "Member Confirmed": '#e53935', // Red
  'Member Confirmed': '#43a047', // Green
};

/** The network a provider was chosen from, with its logo, as the steerage platform shows it. */
function NetworkCell({ name, logoUrl }: { name?: string | null; logoUrl?: string | null }) {
  if (!name) return <span style={{ color: '#757575' }}>Out of network</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {logoUrl && (
        <img
          src={logoUrl}
          alt=""
          style={{ maxHeight: 24, maxWidth: 64, objectFit: 'contain' }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
      )}
      <span>{name}</span>
    </span>
  );
}

function ProviderTypeChip({ value }: { value: string }) {
  const color = providerTypeColors[value] || '#757575'; // Default grey
  return (
    <span
      style={{
        // color: '#fff',
        color: color,
        borderRadius: 8,
        padding: '2px 10px',
        fontWeight: 500,
        fontSize: 13,
        display: 'inline-block',
      }}
    >
      {value}
    </span>
  );
}

function AdmissionStatusChip({ value }: { value: string }) {
  const color = admissionStatusColors[value] || '#e53935'; // Default grey
  return (
    <span
      style={{
        // color: '#fff',
        color: color,
        borderRadius: 8,
        padding: '2px 10px',
        fontWeight: 500,
        fontSize: 13,
        display: 'inline-block',
      }}
    >
      {value}
    </span>
  );
}

/**
 * Details page for Steerage that displays the steerage table
 */
export function SteerageDetailsPage() {
  const [filters, setFilters] = useState<SteerageFilters>(DEFAULT_STEERAGE_FILTERS);
  const [queryState, setQueryState] = useState<QueryState>({ page: 1, pageSize: 100 });
  // Only the first load replaces the page with a loading message; after that
  // the filter bar stays mounted so a search never loses its cursor.
  const [hasLoaded, setHasLoaded] = useState(false);
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [pageData, setPageData] = useState<SteeragePageData>({
    entities: [],
    isLoading: true,
    error: null,
    pagination: {
      page: 1,
      pageSize: 100,
      total: 0,
    totalPages: 0,
    },
  });
  const [currentTenant, setCurrentTenant] = useState<number>(0);
  const show = useSnackbar();
  
  const { role } = user || { role: '' };

  const columns: FFTableColumn<DisplaySteerageData>[] = [
    {
      id: 'caseId',
      label: t('Case ID'),
      width: 110,
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      id: 'createdAt',
      label: t('Date'),
      width: 120,
      type: 'date',
      sortable: true,
    },
    {
      id: 'providerType',
      label: t('Provider Type'),
      width: 120,
      type: 'text',
      sortable: true,
      filterable: true,
      renderCell: (value) => <ProviderTypeChip value={value} />,
    },
    {
      id: 'policyNumber',
      label: t('Policy Number'),
      width: 120,
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      // Shown only: the list's sorting and filtering run on the server, which has no
      // network field to sort or filter by.
      id: 'networkName',
      label: t('Network'),
      width: 170,
      type: 'text',
      renderCell: (value, row) => <NetworkCell name={value} logoUrl={row?.networkLogoUrl} />,
    },
    {
      id: 'providerName',
      label: t('Provider Name'),
      width: 200,
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      id: 'providerAddress',
      label: t('Provider Address'),
      width: 250,
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      id: 'steeredBy',
      label: t('Steered By'),
      width: 120,
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      id: 'admissionStatus',
      label: t('Admission Status'),
      width: 150,
      type: 'text',
      filterable: true,
      renderCell: (value) => <AdmissionStatusChip value={value} />,
    },
  ];

  // Page, size and sort travel together with the filters, so paging or
  // sorting never drops a search (it used to).
  const fetchData = useCallback(
    async (query: QueryState) => {
      try {
        setPageData((prev) => ({ ...prev, isLoading: true }));

        const response = await getAllSteerages(
          {
            page: query.page,
            pageSize: query.pageSize,
            sortBy: query.sortBy,
            sortOrder: query.sortOrder,
            ...listQuery(filters),
          },
          currentTenant
        );

        setPageData({
          entities: response.steerages,
          isLoading: false,
          error: null,
          pagination: response.pagination,
        });
        setHasLoaded(true);
        setQueryState(query);
      } catch (error) {
        const e = error as Error;
        setPageData((prev) => ({
          ...prev,
          isLoading: false,
          error: e,
        }));
        show('Failed to load steerage data', Severity.error);
      }
    },
    [show, filters, currentTenant]
  );

  // Any change to what is being asked for starts again from the first page.
  // Keyed on the inputs rather than the callback, whose identity follows the
  // snackbar's `show` and would otherwise reload after every message.
  useEffect(() => {
    fetchData({ ...queryState, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentTenant]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      fetchData({ ...queryState, page: newPage });
    },
    [fetchData, queryState]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      fetchData({ ...queryState, page: 1, pageSize: newPageSize });
    },
    [fetchData, queryState]
  );

  const onActionClickHandler = useCallback(
    (steerageId: string | number, actionType: string) => {
      const row = pageData.entities.find((r) => r.id === steerageId);
      console.log('Selected steerage:', row, 'Action:', actionType);
    },
    [pageData]
  );

  const handleDownloadExcel = async () => {
    // Prepare data for Excel (remove any fields you don't want exported)
    // The export is what is on screen: same search, filters and tenant.
    const response = await getAllSteerages(
      {
        page: 1,
        pageSize: 1000, // Fetch all for export
        ...listQuery(filters),
      },
      currentTenant
    );
    // The logo stays out of the sheet: it can be a data URI longer than a cell holds.
    const worksheet = XLSX.utils.json_to_sheet(
      response.steerages.map((steerage) => {
        const row: Record<string, unknown> = {
          ...steerage,
          networkName: steerage.networkName || 'Out of network',
        };
        delete row.networkLogoUrl;
        return row;
      })
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Steerage');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'steerage-data.xlsx');
  };

  if (!user) {
    return <div>{t('Loading')}..</div>;
  }

  if (user.status === 'new') {
    return (
      <Container>
        <Typography variant="h4" component="p">
          Welcome to FairFare Dashboard!
        </Typography>
        <Typography component="p">
          To activate your account and access all FairFare features please
          contact us
        </Typography>
      </Container>
    );
  }

  if (!hasLoaded && pageData.isLoading) {
    return <div>{t('Loading')}..</div>;
  }

  if (pageData.error) {
    return (
      <div>
        {t('Error loading steerage data')}: {pageData.error.message}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '100vw', margin: '0 auto', overflow: 'auto' }}>
      <Box display="flex" justifyContent="flex-end">
        <MDButton variant="gradient" color="info" onClick={handleDownloadExcel}>
          {t('Download as Excel')}
        </MDButton>
      </Box>
      <ListFilterBar
        filters={filters}
        defaults={DEFAULT_STEERAGE_FILTERS}
        onChange={setFilters}
        total={pageData.pagination.total}
        noun="steerages"
        searchPlaceholder="Search by case ID, policy number, provider or steered by"
        selects={STEERAGE_SELECTS}
        dateLabel="Date"
        dateOptions={PAST_DATE_OPTIONS}
        tenantSlot={
          role === 'super_user' ? (
            <TenantSelector
              value={currentTenant}
              onChange={setCurrentTenant}
              label={t('Tenant')}
              compact
            />
          ) : undefined
        }
      />
      <FFTable
        columns={columns}
        rows={pageData.entities}
        onActionClick={onActionClickHandler}
        pagination={{
          page: pageData.pagination.page,
          pageSize: pageData.pagination.pageSize,
          total: pageData.pagination.total,
          totalPages: pageData.pagination.totalPages,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
          pageSizeOptions: [50, 100],
        }}
        serverSide={true}
        loading={pageData.isLoading} // We handle loading state separately
        error={pageData.error}
        onSortChange={(orderBy, order) => {
          fetchData({ ...queryState, page: 1, sortBy: orderBy, sortOrder: order });
        }}
        rowKey="id"
        emptyStateMessage={t('No steerage records found')}
        errorStateMessage={t('Error loading steerage data')}
        loadingStateMessage={t('Loading steerage data...')}
      />
    </div>
  );
}

export default SteerageDetailsPage;
